import React from 'react';
import { useState } from 'react';
import {
  Alert,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  FormGroup,
  Box,
  Typography
} from '@mui/material';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../redux/slices/notificationSlice';
import CustomButton from '../../../componentsII/Buttons';
import api from '../../../axios/api';

// Define validation schema
const schema = yup.object().shape({
  typeName: yup
    .string()
    .required('Type name is required')
    .min(2, 'Type name must be at least 2 characters long')
    .max(50, 'Type name must be less than 50 characters')
    .trim(),
  usesFuel: yup
    .boolean()
    .required('Uses fuel selection is required'),
  usesRefrigerant: yup
    .boolean()
    .required('Uses refrigerant selection is required'),
  usesIndustrialGas: yup
    .boolean()
    .required('Uses industrial gas selection is required')
});

const AddUpdateEquipmentTypeModal = ({ onClose, refetch, typeId = null, initialData = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  // Form state
  const [typeName, setTypeName] = useState(initialData?.type_name || '');
  const [usesFuel, setUsesFuel] = useState(initialData?.use_fuel ? 1 : false);
  const [usesRefrigerant, setUsesRefrigerant] = useState(initialData?.use_refrigerant ? 1 : false);
  const [usesIndustrialGas, setUsesIndustrialGas] = useState(initialData?.use_ind_gas ? 1 : false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');
    setError('');

    try {
      // Validate using Yup schema
      await schema.validate({
        typeName,
        usesFuel,
        usesRefrigerant,
        usesIndustrialGas
      }, { abortEarly: true });

      setIsSubmitting(true);

      const payload = {
        typeId: typeId || 0,
        typeName: typeName.trim(),
        usesFuel,
        usesRefrigerant,
        usesIndustrialGas
      };

      const response = await api.post('/admin/CreateUpdateEquipmentType', payload);
      if (response.data.status == '200') {
        dispatch(showNotification({
          message: response.data.message,
          type: "success"
        }));
      }
      onClose();
      refetch();
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setValidationError(err.message);
      } else {
        dispatch(showNotification({
          message: err.message,
          type: "error"
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setTypeName(e.target.value);
    setValidationError(''); // Clear validation error when user starts typing
  };

  return (
    <Card className="w-full mx-auto">
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="typeName" className="text-sm font-medium">
              Type Name
            </label>
            <TextField
              id="typeName"
              value={typeName}
              onChange={handleInputChange}
              placeholder="Enter type name"
              disabled={isSubmitting}
              size='small'
              className={`w-full ${validationError ? 'border-red-500' : ''}`}
            />
            {validationError && (
              <p className="text-sm text-red-500 mt-1">{validationError}</p>
            )}
          </div>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              mt: 3
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Uses Fuel
              </Typography>
              <Switch
                checked={usesFuel}
                onChange={(e) => setUsesFuel(e.target.checked)}
                disabled={isSubmitting}
                color='success'
              />
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Uses Refrigerant
              </Typography>
              <Switch
                checked={usesRefrigerant}
                onChange={(e) => setUsesRefrigerant(e.target.checked)}
                disabled={isSubmitting}
                color='success'
              />
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Uses Industrial Gas
              </Typography>
              <Switch
                checked={usesIndustrialGas}
                onChange={(e) => setUsesIndustrialGas(e.target.checked)}
                disabled={isSubmitting}
                color='success'
              />
            </Box>
          </Box>

          <div className="flex justify-end gap-5 items-center mt-5">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={typeId ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddUpdateEquipmentTypeModal;